import { useQuery, useMutation } from "react-query";

import { isExperianProvider } from "manager/pages/ExperianAuth/utils";

import { NOTIFICATIONS } from "shared/config/constants";
import {
  getExperianAuthToken,
  getExperianAuthTokenByKiq,
  getExperianAuthTokenByOtp,
} from "shared/lib/api/experianAuthApi";
import { openNotification } from "shared/utils/ui";

import {
  ExperianAuthOtpParams,
  ExperianAuthTokenParams,
} from "../../interfaces/api/experianAuth";

/** Company shape for Experian auth */
type CompanyWithExperianToggle = {
  id?: number;
  slug?: string;
  useExperianAuthWithCompanyVrn?: boolean;
};

export const useGetExperianAuthToken = (
  company?: CompanyWithExperianToggle
) => {
  const [requestExperianAuthToken] = useMutation(
    (params?: ExperianAuthTokenParams) => {
      if (company?.useExperianAuthWithCompanyVrn) {
        return getExperianAuthToken(params, company);
      }
      return getExperianAuthToken(params);
    }
  );

  return { requestExperianAuthToken };
};

export const useGetExperianAuthState = (
  company: CompanyWithExperianToggle | undefined = undefined
) => {
  const { data, isLoading } = useQuery(
    [
      `GET_EXPERIAN_AUTH_TOKEN`,
      company?.id,
      company?.useExperianAuthWithCompanyVrn,
    ],
    () => {
      if (company?.useExperianAuthWithCompanyVrn) {
        return getExperianAuthToken(undefined, company);
      }
      return getExperianAuthToken(undefined);
    },
    {
      enabled: !!company && isExperianProvider(company),
      cacheTime: 5000,
      staleTime: 5000,
    }
  );

  return {
    experianAuth: data,
    isExperianLoading: isLoading,
  };
};

export const useGetExperianAuthTokenByOtp = () => {
  const [requestExperianAuthTokenByOtp] = useMutation(
    (params: ExperianAuthOtpParams) => getExperianAuthTokenByOtp(params),
    {
      onError: () => {
        openNotification(
          "Failed to submit information, please try again",
          NOTIFICATIONS.error
        );
      },
    }
  );

  return { requestExperianAuthTokenByOtp };
};

export const useGetExperianAuthTokenByKiq = () => {
  const [requestExperianAuthTokenByKiq] = useMutation(
    (params: Record<string, number | string>) =>
      getExperianAuthTokenByKiq(params),
    {
      onError: () => {
        openNotification(
          "Failed to submit information, please try again",
          NOTIFICATIONS.error
        );
      },
    }
  );

  return { requestExperianAuthTokenByKiq };
};
